import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook for monitoring component performance and user interactions
 * Provides detailed metrics for optimization and debugging
 */
export const usePerformanceMonitoring = (componentName, dependencies = []) => {
  const renderStartTime = useRef(null);
  const renderCount = useRef(0);
  const slowRenderThreshold = 100; // ms
  const verySlowRenderThreshold = 500; // ms

  // Track render start time
  useEffect(() => {
    renderStartTime.current = performance.now();
    renderCount.current++;
  });

  // Measure and log render time
  useEffect(() => {
    if (renderStartTime.current) {
      const renderTime = performance.now() - renderStartTime.current;
      
      if (process.env.NODE_ENV === 'development') {
        const emoji = renderTime > verySlowRenderThreshold ? '🐌' : 
                     renderTime > slowRenderThreshold ? '⚠️' : '✅';
        
        console.log(`${emoji} ${componentName} render #${renderCount.current}: ${renderTime.toFixed(2)}ms`);
        
        // Detailed warnings for slow renders
        if (renderTime > verySlowRenderThreshold) {
          console.warn(`🐌 Very slow render detected in ${componentName}:`, {
            renderTime: `${renderTime.toFixed(2)}ms`,
            renderCount: renderCount.current,
            threshold: `>${verySlowRenderThreshold}ms`,
            suggestion: 'Consider memoization, virtualization, or component splitting'
          });
        } else if (renderTime > slowRenderThreshold) {
          console.warn(`⚠️ Slow render detected in ${componentName}:`, {
            renderTime: `${renderTime.toFixed(2)}ms`,
            renderCount: renderCount.current,
            threshold: `>${slowRenderThreshold}ms`,
            suggestion: 'Consider optimizing this component'
          });
        }
      }

      // TODO: Send metrics to monitoring service in production
      if (process.env.NODE_ENV === 'production' && renderTime > slowRenderThreshold) {
        // Example: trackMetric('component_render_time', renderTime, { 
        //   component: componentName,
        //   renderCount: renderCount.current,
        //   isSlow: renderTime > slowRenderThreshold
        // });
      }
    }
  }, dependencies);

  // Track user actions with performance context
  const trackUserAction = useCallback((action, metadata = {}) => {
    const timestamp = performance.now();
    const actionId = `${componentName}_${action}_${Date.now()}`;
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`👤 User action in ${componentName}:`, {
        action,
        timestamp: timestamp.toFixed(2),
        renderCount: renderCount.current,
        ...metadata
      });
    }

    // TODO: Send to analytics service in production
    // trackEvent('user_action', { 
    //   component: componentName, 
    //   action, 
    //   actionId,
    //   renderCount: renderCount.current,
    //   ...metadata 
    // });

    return actionId;
  }, [componentName]);

  // Track async operations (like API calls)
  const trackAsyncOperation = useCallback((operationName, promise) => {
    const startTime = performance.now();
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`⏳ Starting ${operationName} in ${componentName}`);
    }

    return promise
      .then(result => {
        const duration = performance.now() - startTime;
        
        if (process.env.NODE_ENV === 'development') {
          console.log(`✅ Completed ${operationName} in ${componentName}: ${duration.toFixed(2)}ms`);
        }

        // TODO: Track successful operations
        // trackMetric('async_operation_duration', duration, {
        //   component: componentName,
        //   operation: operationName,
        //   operationId,
        //   success: true
        // });

        return result;
      })
      .catch(error => {
        const duration = performance.now() - startTime;
        
        if (process.env.NODE_ENV === 'development') {
          console.error(`❌ Failed ${operationName} in ${componentName}: ${duration.toFixed(2)}ms`, error);
        }

        // TODO: Track failed operations
        // trackMetric('async_operation_duration', duration, {
        //   component: componentName,
        //   operation: operationName,
        //   operationId,
        //   success: false,
        //   error: error.message
        // });

        throw error;
      });
  }, [componentName]);

  // Memory usage tracking (experimental)
  const trackMemoryUsage = useCallback(() => {
    if (performance.memory && process.env.NODE_ENV === 'development') {
      const memory = performance.memory;
      console.log(`🧠 Memory usage in ${componentName}:`, {
        used: `${(memory.usedJSHeapSize / 1024 / 1024).toFixed(2)} MB`,
        total: `${(memory.totalJSHeapSize / 1024 / 1024).toFixed(2)} MB`,
        limit: `${(memory.jsHeapSizeLimit / 1024 / 1024).toFixed(2)} MB`,
        renderCount: renderCount.current
      });

      // Warn about high memory usage
      const usagePercent = (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100;
      if (usagePercent > 80) {
        console.warn(`🚨 High memory usage detected in ${componentName}: ${usagePercent.toFixed(1)}%`);
      }
    }
  }, [componentName]);

  return { 
    trackUserAction, 
    trackAsyncOperation, 
    trackMemoryUsage,
    renderCount: renderCount.current
  };
};

/**
 * Hook for monitoring modal-specific performance metrics
 */
export const useModalPerformanceMonitoring = (modalName, isOpen) => {
  const openTime = useRef(null);
  const { trackUserAction, trackAsyncOperation } = usePerformanceMonitoring(modalName, [isOpen]);

  // Track modal open/close times
  useEffect(() => {
    if (isOpen) {
      openTime.current = performance.now();
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`🚪 Modal opened: ${modalName}`);
      }
      
      trackUserAction('modal_opened');
    } else if (openTime.current) {
      const sessionDuration = performance.now() - openTime.current;
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`🚪 Modal closed: ${modalName} (session: ${sessionDuration.toFixed(2)}ms)`);
      }
      
      trackUserAction('modal_closed', { sessionDuration });
      openTime.current = null;
    }
  }, [isOpen, modalName, trackUserAction]);

  // Track modal-specific actions
  const trackModalAction = useCallback((action, metadata = {}) => {
    const sessionTime = openTime.current ? performance.now() - openTime.current : 0;
    return trackUserAction(action, { 
      ...metadata, 
      sessionTime: sessionTime.toFixed(2),
      modalName 
    });
  }, [trackUserAction, modalName]);

  return {
    trackModalAction,
    trackAsyncOperation,
    sessionDuration: openTime.current ? performance.now() - openTime.current : 0
  };
};

/**
 * Global performance monitoring utilities
 */
export const PerformanceMonitor = {
  // Track page load performance
  trackPageLoad: () => {
    if (typeof window !== 'undefined' && window.performance) {
      const navigation = performance.getEntriesByType('navigation')[0];
      
      if (navigation && process.env.NODE_ENV === 'development') {
        console.log('📊 Page Load Performance:', {
          domContentLoaded: `${navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart}ms`,
          loadComplete: `${navigation.loadEventEnd - navigation.loadEventStart}ms`,
          totalTime: `${navigation.loadEventEnd - navigation.fetchStart}ms`
        });
      }
    }
  },

  // Get current performance metrics
  getCurrentMetrics: () => {
    if (typeof window === 'undefined' || !window.performance) {
      return null;
    }

    const memory = performance.memory;
    const navigation = performance.getEntriesByType('navigation')[0];

    return {
      memory: memory ? {
        used: Math.round(memory.usedJSHeapSize / 1024 / 1024),
        total: Math.round(memory.totalJSHeapSize / 1024 / 1024),
        limit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024)
      } : null,
      navigation: navigation ? {
        domContentLoaded: Math.round(navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart),
        loadComplete: Math.round(navigation.loadEventEnd - navigation.loadEventStart)
      } : null,
      timestamp: Date.now()
    };
  }
};
